import type { VoiceProfile } from "../types/app";

let context: AudioContext | undefined;

export async function resumeAudioContext(): Promise<AudioContext | null> {
  const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return null;
  if (!context) context = new AudioContextCtor();
  if (context.state !== "running") await context.resume();
  return context;
}

export async function createProcessedAudio(stream: MediaStream, profile: VoiceProfile): Promise<void> {
  const ctx = await resumeAudioContext();
  if (!ctx) throw new Error("AudioContext is unavailable");
  const source = ctx.createMediaStreamSource(stream);
  const warmth = ctx.createBiquadFilter();
  warmth.type = "lowshelf";
  warmth.frequency.value = 260;
  warmth.gain.value = (profile.warmth - 0.5) * 10;
  const brightness = ctx.createBiquadFilter();
  brightness.type = "highshelf";
  brightness.frequency.value = 2800;
  brightness.gain.value = (profile.brightness + profile.breathiness - 0.8) * 8;
  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -22 - profile.compression * 18;
  compressor.ratio.value = 2 + profile.compression * 8;
  compressor.attack.value = 0.01;
  compressor.release.value = 0.2 + profile.softness * 0.35;
  const gain = ctx.createGain();
  gain.gain.value = profile.gain;

  let tail: AudioNode = compressor;
  const lowPower = isLikelyMobileSafari();
  if (!lowPower && "audioWorklet" in ctx && profile.pitch !== 1) {
    try {
      await ctx.audioWorklet.addModule("/pitch-shift-worklet.js");
      const pitchNode = new AudioWorkletNode(ctx, "pitch-shift-worklet", {
        parameterData: { pitch: profile.pitch },
      });
      compressor.connect(pitchNode);
      tail = pitchNode;
    } catch {
      tail = compressor;
    }
  }

  if (!lowPower && profile.reverb > 0.05) {
    const convolver = ctx.createConvolver();
    convolver.buffer = createImpulse(ctx, profile.reverb);
    tail.connect(convolver);
    tail = convolver;
  }

  source.connect(warmth).connect(brightness).connect(compressor);
  tail.connect(gain).connect(ctx.destination);
}

function createImpulse(ctx: AudioContext, amount: number): AudioBuffer {
  const length = Math.floor(ctx.sampleRate * Math.min(1.8, 0.25 + amount * 1.2));
  const buffer = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = buffer.getChannelData(channel);
    for (let index = 0; index < length; index += 1) {
      data[index] = (Math.random() * 2 - 1) * Math.pow(1 - index / length, 2.2) * amount;
    }
  }
  return buffer;
}

function isLikelyMobileSafari(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) || navigator.hardwareConcurrency <= 4;
}
